import type { ApiResponse, AuthSession, UserWithContext as User } from "@platform/types";

export interface ApiClientOptions {
  baseUrl: string;
  getAccessToken?: () => string | null;
  getRefreshToken?: () => string | null;
  onTokensRefreshed?: (session: Pick<AuthSession, "accessToken" | "refreshToken">) => void;
  onAuthExpired?: () => void;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export class ApiClient {
  private refreshPromise: Promise<string | null> | null = null;

  constructor(private options: ApiClientOptions) {}

  private async request<T>(path: string, init: RequestInit = {}, retry = true): Promise<T> {
    const token = this.options.getAccessToken?.();
    const res = await fetch(`${this.options.baseUrl}${path}`, {
      ...init,
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...init.headers,
      },
    });

    const body = (await res.json().catch(() => null)) as ApiResponse<T> | null;

    if (res.status === 401 && retry && this.options.getRefreshToken?.()) {
      const newToken = await this.refreshAccessToken();
      if (newToken) return this.request<T>(path, init, false);
      this.options.onAuthExpired?.();
    }

    if (!res.ok || !body || body.success === false) {
      const err = body && body.success === false ? body.error : null;
      throw new ApiError(res.status, err?.code ?? "UNKNOWN_ERROR", err?.message ?? res.statusText, err?.details);
    }

    return body.data;
  }

  private async refreshAccessToken(): Promise<string | null> {
    if (this.refreshPromise) return this.refreshPromise;
    this.refreshPromise = (async () => {
      try {
        const refreshToken = this.options.getRefreshToken?.();
        if (!refreshToken) return null;
        const res = await fetch(`${this.options.baseUrl}/v1/auth/refresh`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refreshToken }),
        });
        const body = (await res.json()) as ApiResponse<{ accessToken: string; refreshToken: string }>;
        if (!res.ok || body.success === false) return null;
        this.options.onTokensRefreshed?.(body.data);
        return body.data.accessToken;
      } finally {
        this.refreshPromise = null;
      }
    })();
    return this.refreshPromise;
  }

  // ── Auth ──────────────────────────────────────────────────────────────────
  auth = {
    register: (input: { email: string; password: string; name: string }) =>
      this.request<{ requiresVerification: boolean; email: string }>("/v1/auth/register", { method: "POST", body: JSON.stringify(input) }),
    login: (input: { email: string; password: string }) =>
      this.request<AuthSession>("/v1/auth/login", { method: "POST", body: JSON.stringify(input) }),
    google: (idToken: string) =>
      this.request<AuthSession>("/v1/auth/google", { method: "POST", body: JSON.stringify({ idToken }) }),
    logout: () => this.request<{ ok: true }>("/v1/auth/logout", { method: "POST" }),
    me: () => this.request<User>("/v1/auth/me"),
    forgotPassword: (email: string) =>
      this.request<{ ok: boolean }>("/v1/auth/forgot-password", { method: "POST", body: JSON.stringify({ email }) }),
    resetPassword: (token: string, newPassword: string) =>
      this.request<{ ok: boolean }>("/v1/auth/reset-password", { method: "POST", body: JSON.stringify({ token, newPassword }) }),
    verifyEmail: (token: string) =>
      this.request<{ ok: boolean }>("/v1/auth/verify-email", { method: "POST", body: JSON.stringify({ token }) }),
    resendVerification: (email: string) =>
      this.request<{ ok: boolean }>("/v1/auth/resend-verification", { method: "POST", body: JSON.stringify({ email }) }),
  };


  // ── SSO ────────────────────────────────────────────────────────────────────
  sso = {
    checkSso: (email: string, enterpriseSlug: string) =>
      this.request<{ ssoEnabled: boolean; providerName?: string; protocol?: string; initiateUrl?: string }>(
        "/v1/auth/sso/check", { method: "POST", body: JSON.stringify({ email, enterpriseSlug }) }
      ),
    getConfig: (enterpriseId: string) =>
      this.request<unknown>(`/v1/auth/sso/${enterpriseId}/config`),
    saveConfig: (enterpriseId: string, input: {
      protocol: "oidc" | "saml"; providerName: string;
      clientId?: string; clientSecret?: string;
      domainHint?: string; metadataXml?: string; issuerUrl?: string;
    }) =>
      this.request<{ ok: true; id: string }>(`/v1/auth/sso/${enterpriseId}/config`, {
        method: "POST", body: JSON.stringify(input),
      }),
    setStatus: (enterpriseId: string, status: "active" | "disabled") =>
      this.request<{ ok: true }>(`/v1/auth/sso/${enterpriseId}/status`, {
        method: "PATCH", body: JSON.stringify({ status }),
      }),
    testConnection: (enterpriseId: string) =>
      this.request<{ passed: boolean; detail: string }>(`/v1/auth/sso/${enterpriseId}/test`, { method: "POST" }),
    deleteConfig: (enterpriseId: string) =>
      this.request<{ ok: true }>(`/v1/auth/sso/${enterpriseId}/config`, { method: "DELETE" }),
  };

  // ── Enterprise ────────────────────────────────────────────────────────────
  enterprise = {
    listUsers: () => this.request<User[]>("/v1/enterprise/users"),
    approveUser: (userId: string) =>
      this.request<{ ok: true }>(`/v1/enterprise/users/${userId}/approve`, { method: "POST" }),
    declineUser: (userId: string) =>
      this.request<{ ok: true }>(`/v1/enterprise/users/${userId}/decline`, { method: "POST" }),
    getUser: (userId: string) => this.request<unknown>(`/v1/enterprise/users/${userId}`),
    resetPassword: (userId: string) =>
      this.request<{ tempPassword: string }>(`/v1/enterprise/users/${userId}/reset-password`, { method: "POST" }),
    unlockUser: (userId: string) =>
      this.request<{ ok: true }>(`/v1/enterprise/users/${userId}/unlock`, { method: "POST" }),
    assignRoles: (userId: string, roleIds: string[]) =>
      this.request<{ ok: true }>(`/v1/enterprise/users/${userId}/roles`, { method: "PATCH", body: JSON.stringify({ roleIds }) }),
    inviteUser: (input: {
      firstName: string; lastName: string; email: string;
      jobTitle?: string; appKeys: string[]; roleIds: string[];
    }) => this.request<{ userId: string; email: string }>("/v1/enterprise/users/invite", { method: "POST", body: JSON.stringify(input) }),

    listRoles: () => this.request<unknown[]>("/v1/enterprise/roles"),
    listModules: () => this.request<unknown[]>("/v1/enterprise/modules"),
    toggleModule: (moduleId: string, enabled: boolean) =>
      this.request<{ ok: true }>("/v1/enterprise/modules", { method: "PATCH", body: JSON.stringify({ moduleId, enabled }) }),

    getSettings: () => this.request<{ id: string; name: string; slug: string; status: string }>("/v1/enterprise/settings"),
    updateSettings: (input: { name?: string }) =>
      this.request<{ ok: true }>("/v1/enterprise/settings", { method: "PATCH", body: JSON.stringify(input) }),
    dashboard: () => this.request<{
      total: number; active: number; pending: number;
      pendingUsers: { id: string; name: string; email: string; requestedAt: string }[];
      recentActivity: unknown[];
    }>("/v1/enterprise/dashboard"),
  };

  // ── Profile ───────────────────────────────────────────────────────────────
  profile = {
    get: () => this.request<User>("/v1/profile"),
    update: (input: { name?: string; jobTitle?: string; timezone?: string; dateFormat?: string }) =>
      this.request<User>("/v1/profile", { method: "PATCH", body: JSON.stringify(input) }),
    changePassword: (input: { currentPassword: string; newPassword: string }) =>
      this.request<{ ok: true }>("/v1/profile/change-password", { method: "POST", body: JSON.stringify(input) }),
    listSessions: () => this.request<{ id: string; createdAt: string; expiresAt: string }[]>("/v1/profile/sessions"),
    revokeSession: (sessionId: string) =>
      this.request<{ ok: true }>(`/v1/profile/sessions/${sessionId}`, { method: "DELETE" }),
    revokeAllOtherSessions: (currentSessionId: string) =>
      this.request<{ ok: true }>("/v1/profile/sessions", { method: "DELETE", body: JSON.stringify({ currentSessionId }) }),
  };

  // ── Platform ──────────────────────────────────────────────────────────────
  platform = {
    listEnterprises: () => this.request<unknown[]>("/v1/platform/enterprises"),
    getEnterprise: (id: string) => this.request<unknown>(`/v1/platform/enterprises/${id}`),
    getEnterpriseUsers: (id: string) => this.request<unknown[]>(`/v1/platform/enterprises/${id}/users`),
    getEnterpriseRoles: (id: string) => this.request<unknown[]>(`/v1/platform/enterprises/${id}/roles`),
    getEnterpriseModules: (id: string) => this.request<unknown[]>(`/v1/platform/enterprises/${id}/modules`),
    toggleEnterpriseModule: (id: string, moduleId: string, enabled: boolean) =>
      this.request<{ ok: true }>(`/v1/platform/enterprises/${id}/modules`, { method: "PATCH", body: JSON.stringify({ moduleId, enabled }) }),
    updateEnterpriseSettings: (id: string, input: { name?: string; allowedDomains?: string[] }) =>
      this.request<{ ok: true }>(`/v1/platform/enterprises/${id}/settings`, { method: "PATCH", body: JSON.stringify(input) }),
    createEnterprise: (input: { name: string; slug: string; adminEmail: string; moduleKeys: string[] }) =>
      this.request<unknown>("/v1/platform/enterprises", { method: "POST", body: JSON.stringify(input) }),
    enterpriseStorage: (id: string) =>
      this.request<{ usedBytes: number; totalLimitBytes: number | null; perUserLimitBytes: number | null }>(`/v1/platform/enterprises/${id}/storage`),
    updateEnterpriseStorage: (id: string, input: { userStorageLimitBytes?: number | null; totalStorageLimitBytes?: number | null }) =>
      this.request<{ usedBytes: number; totalLimitBytes: number | null; perUserLimitBytes: number | null }>(`/v1/platform/enterprises/${id}/storage`, { method: "PATCH", body: JSON.stringify(input) }),
    updateUserStorageLimit: (userId: string, storageLimitBytes: number | null) =>
      this.request<{ usedBytes: number; quotaBytes: number }>(`/v1/platform/users/${userId}/storage-limit`, { method: "PATCH", body: JSON.stringify({ storageLimitBytes }) }),
    myStorage: () =>
      this.request<{ usedBytes: number; quotaBytes: number; isPlatformAdmin: boolean }>("/v1/platform/me/storage"),
    suspendEnterprise: (id: string) =>
      this.request<{ ok: true }>(`/v1/platform/enterprises/${id}/suspend`, { method: "PATCH" }),
    reinstateEnterprise: (id: string) =>
      this.request<{ ok: true }>(`/v1/platform/enterprises/${id}/reinstate`, { method: "PATCH" }),
    resetEnterprise: (id: string) =>
      this.request<{ ok: true }>(`/v1/platform/enterprises/${id}/reset`, { method: "DELETE" }),

    listUsers: () => this.request<unknown[]>("/v1/platform/users"),
    createUser: (input: { name: string; email: string; jobTitle?: string }) =>
      this.request<{ id: string; name: string; email: string; tempPassword: string }>(
        "/v1/platform/users", { method: "POST", body: JSON.stringify(input) },
      ),
    getUser: (id: string) => this.request<unknown>(`/v1/platform/users/${id}`),
    resetUserPassword: (id: string) =>
      this.request<{ tempPassword: string }>(`/v1/platform/users/${id}/reset-password`, { method: "POST" }),
    unlockUser: (id: string) =>
      this.request<{ ok: true }>(`/v1/platform/users/${id}/unlock`, { method: "POST" }),
    setPlatformRole: (id: string, platformRole: "platform_admin" | "support" | null) =>
      this.request<{ ok: true }>(`/v1/platform/users/${id}/platform-role`, { method: "PATCH", body: JSON.stringify({ platformRole }) }),
    setDefaultEnterprise: (userId: string, enterpriseId: string) =>
      this.request<{ ok: true }>(`/v1/platform/users/${userId}/default-enterprise`, { method: "PATCH", body: JSON.stringify({ enterpriseId }) }),
    deleteUser: (id: string) =>
      this.request<{ ok: true }>(`/v1/platform/users/${id}`, { method: "DELETE" }),
    addToEnterprise: (userId: string, enterpriseId: string, roleIds: string[]) =>
      this.request<{ ok: true }>(`/v1/platform/users/${userId}/enterprises`, { method: "POST", body: JSON.stringify({ enterpriseId, roleIds }) }),
    removeEnterpriseMembership: (userId: string, enterpriseId: string) =>
      this.request<{ ok: true }>(`/v1/platform/users/${userId}/enterprises/${enterpriseId}`, { method: "DELETE" }),
    assignEnterpriseRoles: (userId: string, enterpriseId: string, roleIds: string[]) =>
      this.request<{ ok: true }>(`/v1/platform/users/${userId}/enterprises/${enterpriseId}/roles`, { method: "PATCH", body: JSON.stringify({ roleIds }) }),
    // Platform-level roles — no enterprise. For individual users accessing
    // shared modules (e.g. ConfigIQ) directly on app.tsnet.dev.
    assignPlatformRoles: (userId: string, roleIds: string[]) =>
      this.request<{ ok: true }>(`/v1/platform/users/${userId}/platform-roles`, { method: "PATCH", body: JSON.stringify({ roleIds }) }),

    health: () => this.request<{ uptime: number; memoryMb: number; nodeVersion: string; status: string; disk: { totalGb: number; usedGb: number; freeGb: number; usagePercent: number } | null }>("/v1/platform/health"),
    auditLog: () => this.request<unknown[]>("/v1/platform/audit"),
    listModules: () => this.request<unknown[]>("/v1/platform/modules"),
    listRoles: () => this.request<unknown[]>("/v1/platform/roles"),
    createRole: (input: { key: string; name: string; description?: string; moduleId?: string; individuallyAssignable?: boolean }) =>
      this.request<unknown>("/v1/platform/roles", { method: "POST", body: JSON.stringify(input) }),
    updateRole: (id: string, input: { name?: string; description?: string; permissions?: Record<string, Record<string, boolean>>; individuallyAssignable?: boolean }) =>
      this.request<{ ok: true }>(`/v1/platform/roles/${id}`, { method: "PATCH", body: JSON.stringify(input) }),
    deleteRole: (id: string) =>
      this.request<{ ok: true }>(`/v1/platform/roles/${id}`, { method: "DELETE" }),
    getEmailSettings: () =>
      this.request<{
        provider: string; hasApiKey: boolean; apiKeyLast4: string | null;
        fromEmail: string | null; fromName: string | null; enabled: boolean; monthlyLimit: number | null;
      }>("/v1/platform/email-settings"),
    updateEmailSettings: (input: {
      provider?: string; apiKey?: string; fromEmail?: string; fromName?: string; enabled?: boolean; monthlyLimit?: number | null;
    }) =>
      this.request<{ ok: true }>("/v1/platform/email-settings", { method: "PATCH", body: JSON.stringify(input) }),
    getEmailStats: () =>
      this.request<{ sentThisMonth: number; monthlyLimit: number | null }>("/v1/platform/email-stats"),
  };

  feedback = {
    submit: (input: { type: "feedback" | "bug"; title: string; description: string; draftId?: string }) =>
      this.request<unknown>("/v1/feedback", { method: "POST", body: JSON.stringify(input) }),
    mine: () => this.request<unknown[]>("/v1/feedback/mine"),
    all: () => this.request<unknown[]>("/v1/feedback"),
    updateStatus: (id: string, input: { status: "open" | "under_review" | "resolved" | "closed"; reviewerNote?: string }) =>
      this.request<unknown>(`/v1/feedback/${id}/status`, { method: "PATCH", body: JSON.stringify(input) }),
  };

  notifications = {
    list: () => this.request<{ id: string; type: string; message: string; createdAt: string; readAt: string | null }[]>("/v1/notifications"),
    markRead: (id: string) => this.request<{ ok: true }>(`/v1/notifications/${id}/read`, { method: "PATCH" }),
    markAllRead: () => this.request<{ ok: true }>("/v1/notifications/read-all", { method: "PATCH" }),
  };

  // Platform's own dashboard reaching into ConfigIQ's data — goes through
  // the same /v1/configiq/* proxy every ConfigIQ page uses (see
  // configiq.proxy.ts), just called from Platform's own UI instead of the
  // federated ConfigIQ remote. windowDays omitted = all time.
  configiq = {
    topTasks: (windowDays?: number) =>
      this.request<{ taskId: string; taskName: string; moduleName: string; instanceCount: number; lastUsed: string | null }[]>(
        `/v1/configiq/admin/top-tasks${windowDays ? `?window=${windowDays}` : ""}`,
      ),
  };

  files = {
    list: () =>
      this.request<{
        files: { id: string; fileName: string; mimeType: string; sizeBytes: number; folderPath: string; createdAt: string }[];
        usedBytes: number;
        quotaBytes: number;
      }>("/v1/files"),
    quota: () => this.request<{ usedBytes: number; quotaBytes: number }>("/v1/files/quota"),
    upload: (input: { fileName: string; mimeType: string; contentBase64: string; folderPath?: string }) =>
      this.request<{ id: string; fileName: string; sizeBytes: number }>("/v1/files", { method: "POST", body: JSON.stringify(input) }),
    downloadUrl: (id: string) =>
      this.request<{ url: string; expiresAt: string; fileName: string }>(`/v1/files/${id}/download`),
    /** Fetches the file as a blob with the same JWT the rest of the
     *  client uses — for the authenticated proxy download approach where
     *  the browser can't navigate directly to a protected endpoint
     *  without a way to attach the Authorization header. Returns the
     *  raw Response so the caller can read it as a Blob and trigger a
     *  browser save-as without an intermediate signed URL. */
    downloadBlob: (id: string) => {
      const token = this.options.getAccessToken?.();
      return fetch(`${this.options.baseUrl}/v1/files/${id}/download`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
    },
    delete: (id: string) => this.request<{ ok: true }>(`/v1/files/${id}`, { method: "DELETE" }),
  };

  storageSettings = {
    get: () => this.request<{ defaultQuotaBytes: number }>("/v1/platform/storage-settings"),
    update: (defaultQuotaBytes: number) =>
      this.request<{ defaultQuotaBytes: number }>("/v1/platform/storage-settings", {
        method: "PATCH",
        body: JSON.stringify({ defaultQuotaBytes }),
      }),
  };

  scheduler = {
    list: () => this.request<SchedulerJob[]>("/v1/scheduler/jobs"),
    get: (id: string) => this.request<SchedulerJob & { runs: SchedulerJobRun[] }>(`/v1/scheduler/jobs/${id}`),
    create: (input: CreateSchedulerJobInput) =>
      this.request<SchedulerJob>("/v1/scheduler/jobs", { method: "POST", body: JSON.stringify(input) }),
    update: (
      id: string,
      // description explicitly allows null (not just optional string) to
      // match the backend's own updateScheduledJobSchema, which supports
      // clearing it -- Omit + override rather than just Partial<...>,
      // since Partial alone keeps the create type's string-only shape.
      input: Partial<Omit<CreateSchedulerJobInput, "description">> & { enabled?: boolean; description?: string | null },
    ) =>
      this.request<SchedulerJob>(`/v1/scheduler/jobs/${id}`, { method: "PATCH", body: JSON.stringify(input) }),
    delete: (id: string) => this.request<{ ok: true }>(`/v1/scheduler/jobs/${id}`, { method: "DELETE" }),
    trigger: (id: string) => this.request<SchedulerJobRun>(`/v1/scheduler/jobs/${id}/trigger`, { method: "POST" }),
    runs: () =>
      this.request<(SchedulerJobRun & { jobName: string; jobType: "etl" | "report_generation" })[]>("/v1/scheduler/runs"),
  };
}

export interface SchedulerTriggerConfig {
  expression?: string; // cron
  seconds?: number;    // interval
  at?: string;         // one_time, ISO timestamp
}

export interface SchedulerJob {
  id: string;
  name: string;
  description: string | null;
  jobType: "etl" | "report_generation";
  enterpriseId: string | null;
  createdBy: string;
  triggerType: "cron" | "interval" | "one_time";
  triggerConfig: SchedulerTriggerConfig;
  webhookUrl: string;
  jobConfig: Record<string, unknown>;
  notificationType: "email" | "bell" | "both" | "none";
  enabled: boolean;
  nextRunAt: string | null;
  lastRunAt: string | null;
  createdAt: string;
}

export interface SchedulerJobRun {
  id: string;
  jobId: string;
  status: "running" | "success" | "failed";
  triggeredBy: "scheduled" | "manual";
  startedAt: string;
  finishedAt: string | null;
  resultMessage: string | null;
}

export interface CreateSchedulerJobInput {
  name: string;
  description?: string;
  jobType: "etl" | "report_generation";
  enterpriseId?: string | null;
  triggerType: "cron" | "interval" | "one_time";
  triggerConfig: SchedulerTriggerConfig;
  webhookUrl: string;
  jobConfig: Record<string, unknown>;
  notificationType?: "email" | "bell" | "both" | "none";
}

export function createApiClient(options: ApiClientOptions): ApiClient {
  return new ApiClient(options);
}
